"use client";

/**
 * Client-side page analytics
 *
 * - **Flags (optional):** When `evaluatedFlagProperties` is passed, syncs
 *   `flag_<vercel-key>` values from flags already read on the server in this
 *   request. Only `track` / `page` events fired after that sync include those
 *   properties (via analytics middleware).
 * - **Page view:** Fires Segment `page()` after document load when pathname,
 *   search, template, or flag snapshot changes. Includes BotID `tags` from a
 *   protected Server Action (prefetched and cached for the tab session).
 */

import { getSeoPageProperties, page } from "@homeinstead/analytics";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect } from "react";
import { getCachedBotIdTags } from "./get-cached-bot-id-tags";
import { useEvaluatedFlagsSync } from "./use-evaluated-flags-sync";
import { usePageViewAfterLoad } from "./use-page-view-after-load";
import { useTrackUtmParams } from "./use-track-utm-params";

type PageAnalyticsProps = {
  /**
   * `flag_<vercel-key>` → normalized value from `getEvaluatedFlagAnalyticsProperties()`.
   * Omit on routes that do not evaluate flags (legacy, 404).
   */
  evaluatedFlagProperties?: Record<string, string>;
  pageTemplate?: string;
};

function PageAnalytics({
  evaluatedFlagProperties,
  pageTemplate,
}: PageAnalyticsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const evaluatedFlagPropertiesSnapshotKey =
    evaluatedFlagProperties === undefined
      ? undefined
      : JSON.stringify(evaluatedFlagProperties);

  useEvaluatedFlagsSync(evaluatedFlagProperties);

  // @note marked for deprecation
  useTrackUtmParams();

  // Overlap BotID RTT with remaining document load / flag sync.
  useEffect(() => {
    void getCachedBotIdTags();
  }, []);

  const handlePageView = useCallback(
    async (isCancelled: () => boolean) => {
      const tags = await getCachedBotIdTags();
      if (isCancelled()) {
        return;
      }

      const seoComplianceProperties = getSeoPageProperties({ pathname });

      const currentSearchString = searchParams.toString();
      const queryString = currentSearchString ? `?${currentSearchString}` : "";

      page({
        path: pathname,
        search: currentSearchString,
        url: `${window.location.origin}${pathname}${queryString}`,
        ...seoComplianceProperties,
        ...(pageTemplate ? { pageTemplate } : {}),
        tags,
      });
    },
    [pageTemplate, pathname, searchParams],
  );

  const pageViewTriggerKey = [
    evaluatedFlagPropertiesSnapshotKey ?? "",
    pageTemplate ?? "",
    pathname,
    searchParams.toString(),
  ].join("|");

  usePageViewAfterLoad({
    onPageView: handlePageView,
    triggerKey: pageViewTriggerKey,
  });

  return null;
}

export { PageAnalytics };
